<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { IconProps } from '../icons/icons.types';
  import type { NavPoints } from './navigation.types';

  export const allowedTags = ['li', 'div'] as const;
  export type AllowedTag = (typeof allowedTags)[number];

  export interface Props {
    icon?: IconProps['icon'];
    tag?: AllowedTag;
    href?: string;
    active?: boolean;
    alarm?: boolean;
    count?: number;
    points?: NavPoints;
    subnavigation?: Snippet;
    children?: Snippet;
  }

  export type ActiveReportFn = (id: string, active: boolean) => void;
</script>

<script lang="ts">
  import { getContext, setContext } from 'svelte';
  import * as Icons from '@lucide/svelte';
  import IconComponent from '../../components/icons/Icon.svelte';
  import Navigation from './Navigation.svelte';
  import { PointService } from '../points/PointService';
  import { NavigationHandler } from '../../handlers/NavigationHandler';
  import { slide } from 'svelte/transition';
  import { linear } from 'svelte/easing';

  const { icon, tag, href, active, alarm, count, points, subnavigation, children }: Props = $props();

  // -
  const InternalId = $props.id();
  const ACTIVE_REPORT_KEY = 'nav-active-report';

  // -
  const DefaultTag = allowedTags[0];
  const ElementTag = $derived(tag && allowedTags.includes(tag) ? tag : DefaultTag);

  // -
  const NavigationVariants = new NavigationHandler();
  const IsBar = NavigationVariants.getVariant() === 'bar';

  // -
  const Link = $derived(PointService.buildLink(href));
  const CanClick = $derived(Link.hasLink);

  // -
  const NavIcon = $derived(icon ?? (Link.target ? Icons.ExternalLink : Icons.FlagTriangleRight));
  const IsActive = $derived(active ?? false);

  // -
  const HasSubnavigation = $derived(subnavigation || points);

  // -
  const reportActiveToParent = getContext<ActiveReportFn | undefined>(ACTIVE_REPORT_KEY);
  const childActiveMap = $state<Record<string, boolean>>({});
  const AnyChildActive = $derived(Object.values(childActiveMap).some(Boolean));
  // - the points say it from their data, even while the branch is closed and its children are not mounted
  const AnyPointActive = $derived(hasActivePoint(points));
  const SelfOrDescendantActive = $derived(IsActive || AnyChildActive || AnyPointActive);

  function hasActivePoint(navPoints: NavPoints | undefined): boolean {
    return findActivePoint(navPoints) !== null;
  }

  /** The id of the active point below (`<key>/<key>…`), null without one. */
  function findActivePoint(navPoints: NavPoints | undefined): string | null {
    for (const [key, point] of Object.entries(navPoints ?? {})) {
      if (point.active === true) {
        return key;
      }
      const Below = findActivePoint(point.sub);
      if (Below !== null) {
        return `${key}/${Below}`;
      }
    }

    return null;
  }

  function reportChildActive(childId: string, isActive: boolean): void {
    childActiveMap[childId] = isActive;
  }

  setContext<ActiveReportFn>(ACTIVE_REPORT_KEY, reportChildActive);
  $effect(() => {
    reportActiveToParent?.(InternalId, SelfOrDescendantActive);
  });
  // An unmounted point must not keep its parent branch active.
  $effect(() => {
    return (): void => {
      reportActiveToParent?.(InternalId, false);
    };
  });

  // -
  // - which point of the branch is active: itself, one in its data, or a snippet child — '' for none
  const ActiveId = $derived(IsActive ? '.' : (findActivePoint(points) ?? (AnyChildActive ? '*' : '')));

  // - opens whenever another point of the branch becomes the active one (/admin → /users too), never closes by itself
  let IsOpen = $state(false);
  let LastActiveId = '';
  $effect(() => {
    const NowActiveId = ActiveId;
    if (NowActiveId !== '' && NowActiveId !== LastActiveId) {
      IsOpen = true;
    }
    LastActiveId = NowActiveId;
  });

  function toggleOpen(): void {
    IsOpen = !IsOpen;
  }
</script>

<svelte:element
  this={ElementTag}
  class="navPoint {IsBar ? 'navBar' : 'navTree'} {IsActive ? 'active' : ''} {SelfOrDescendantActive ? 'activeBranch' : ''}"
  data-internal-id={InternalId}>
  <span class="navWrapper {CanClick ? 'canClick' : ''}">
    {#if Link.hasLink}
      <a class="navLink" href={Link.url} target={Link.target} rel={Link.rel}>
        <IconComponent icon={NavIcon} size={1.5} {alarm} {count} {children} />
      </a>
    {:else}
      <span class="navLink">
        <IconComponent icon={NavIcon} size={1.5} {alarm} {count} {children} />
      </span>
    {/if}
    {#if HasSubnavigation && !IsBar}
      <button type="button" class="openClose" class:open={IsOpen} aria-expanded={IsOpen} onclick={toggleOpen} style:cursor={IsOpen ? 'zoom-out' : 'zoom-in'}>
        <IconComponent icon={IsOpen ? Icons.PanelTopOpen : Icons.PanelRightOpen} size={1} />
      </button>
    {/if}
  </span>
  {#if HasSubnavigation && (IsBar || IsOpen)}
    <div class="navSub" transition:slide={{ axis: 'y', easing: linear, duration: 200 }}>
      <Navigation {points}>{@render subnavigation?.()}</Navigation>
    </div>
  {/if}
</svelte:element>

<style lang="scss">
  @use '../../../styles/display';

  .navPoint {
    z-index: 50;

    &,
    & > .navWrapper,
    & > .navWrapper > .navLink {
      @include display.block;
      font-size: var(--nav-font-size, 1em);
      line-height: 1;
      white-space: nowrap;
      position: relative;
      cursor: default;
    }

    :global(+ .navPoint) {
      margin-top: var(--nav-margin-top, 1em);
    }

    &:first-of-type {
      margin-top: var(--nav-margin-top-first, 0);
    }

    > .navWrapper {
      position: relative;
      width: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: 0.25em;
      flex-wrap: nowrap;
      border-radius: var(--nav-border-radius, 0);
      border: var(--nav-border-width, 1px) var(--nav-border-style, solid) var(--nav-border-color, rgba(0, 0, 0, 0));
      box-shadow: var(--nav-box-shadow, none);
      background-color: var(--nav-bg, transparent);

      &.canClick {
        cursor: pointer;

        > .navLink {
          cursor: pointer;
        }
      }

      > .navLink {
        z-index: 10;
        padding: var(--nav-inset-y, 0.25rem) var(--nav-inset-x, 0.5rem);
        overflow: hidden;
        flex: 1;
      }

      > .openClose {
        padding: var(--nav-inset-y, 0.25rem) var(--nav-inset-x, 0.5rem);
        padding-left: 0;
      }
    }

    &.active {
      > .navWrapper {
        background-color: var(--nav-bg-active, var(--layout-mark-bg-full)) !important;
      }
    }

    &:hover {
      z-index: 60;

      > .navWrapper {
        z-index: 20;
        overflow: visible;
        width: max-content;
        min-width: 100%;
        background-color: var(--nav-bg-inactive, var(--layout-mark-bg-inactive));
        border-color: var(--nav-border-color-hover, transparent);
        box-shadow: var(--nav-box-shadow-hover, none);

        &.canClick {
          background-color: var(--nav-bg-active, var(--layout-mark-bg-light));
        }

        > .navLink {
          width: 100%;
          overflow: visible;
        }
      }
    }

    // - Waagerechte Leiste
    &.navBar {
      &:first-of-type {
        margin-top: 0;
      }

      > .navWrapper {
        width: auto;
        background: var(--layout-background-little-main);
      }

      &:hover {
        > .navWrapper {
          width: auto;
          min-width: 0;
        }
      }

      > .navSub {
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 30;
        display: none;
        min-width: 100%;
        padding: 0.5rem;
        margin: 0;
        background: var(--layout-background);
        border: var(--border-basic);
        border-radius: calc(var(--border-radius) / 2);
        box-shadow: var(--box-shadow-basic);

        :global(.nav) {
          margin-left: 0;
        }
      }

      &:hover > .navSub,
      &:focus-within > .navSub {
        display: block;
      }

      &.activeBranch:has(> .navSub) {
        display: contents;

        > .navSub {
          position: static;
          display: block;
          order: 1;
          flex-basis: 100%;
          padding: 0;
          background: none;
          border: none;
          border-radius: 0;
          box-shadow: none;
          margin-top: var(--inset-abs);
        }

        > .navSub :global(.nav) {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem 0.5rem;
        }

        > .navSub :global(.navPoint) {
          margin-top: 0;
        }
      }
    }
  }
</style>
